(function () {
  const DELIVERY_TIME_ZONE = 'Europe/Madrid';
  const DEFAULT_CUTOFF_HOUR = 14;
  const SEARCH_WINDOW_DAYS = 60;
  const DAY_IN_MILLISECONDS = 86400000;
  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const deliveryDateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: DELIVERY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  let deliveryRefreshTimer;

  function normalizeCutoffHour(value) {
    const cutoffHour = Number(value);
    return Number.isFinite(cutoffHour) && cutoffHour >= 0 && cutoffHour <= 23
      ? cutoffHour
      : DEFAULT_CUTOFF_HOUR;
  }

  function createCalendarDate(year, month, day) {
    const timestamp = Date.UTC(year, month - 1, day);
    const date = new Date(timestamp);

    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() + 1 !== month ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return {
      year,
      month,
      day,
      key: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    };
  }

  function normalizeUnavailableDate(value) {
    const dateString = String(value ?? '').trim().replace(/^['"]|['"]$/g, '');
    let match = dateString.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

    if (match) {
      return createCalendarDate(Number(match[1]), Number(match[2]), Number(match[3]))?.key ?? null;
    }

    match = dateString.match(/^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/);
    if (!match) return null;

    const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
    return createCalendarDate(year, Number(match[2]), Number(match[1]))?.key ?? null;
  }

  function normalizeUnavailableDates(value) {
    if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
      return normalizeUnavailableDates(value.value);
    }

    if (Array.isArray(value)) {
      return [...new Set(value.map(normalizeUnavailableDate).filter(Boolean))];
    }

    if (typeof value !== 'string' || value.trim() === '') return [];

    const trimmedValue = value.trim();
    try {
      const parsedValue = JSON.parse(trimmedValue);
      if (parsedValue !== trimmedValue) return normalizeUnavailableDates(parsedValue);
    } catch (_error) {
      // Legacy metafields may be emitted as a comma-separated string instead of JSON.
    }

    return [
      ...new Set(
        trimmedValue
          .replace(/^\[|\]$/g, '')
          .split(',')
          .map(normalizeUnavailableDate)
          .filter(Boolean),
      ),
    ];
  }

  function getDeliveryDateTime(now = new Date()) {
    const parts = deliveryDateTimeFormatter.formatToParts(now);
    const values = {};

    parts.forEach(({ type, value }) => {
      if (type !== 'literal') values[type] = Number(value);
    });

    const calendarDate = createCalendarDate(values.year, values.month, values.day);
    if (!calendarDate) {
      throw new Error(`Unable to determine the current delivery date in ${DELIVERY_TIME_ZONE}.`);
    }

    return {
      calendarDate,
      hour: values.hour,
      minute: values.minute,
      second: values.second,
    };
  }

  function addCalendarDays(calendarDate, numberOfDays) {
    const timestamp = Date.UTC(calendarDate.year, calendarDate.month - 1, calendarDate.day);
    const result = new Date(timestamp + numberOfDays * DAY_IN_MILLISECONDS);
    return createCalendarDate(result.getUTCFullYear(), result.getUTCMonth() + 1, result.getUTCDate());
  }

  function getWeekdayIndex(calendarDate) {
    return new Date(Date.UTC(calendarDate.year, calendarDate.month - 1, calendarDate.day)).getUTCDay();
  }

  function getDeliverySearchStart({ cutoffHour, sameDayEligible, now = new Date() }) {
    const deliveryNow = getDeliveryDateTime(now);
    const canDeliverToday = Boolean(sameDayEligible) && deliveryNow.hour < normalizeCutoffHour(cutoffHour);
    return canDeliverToday ? deliveryNow.calendarDate : addCalendarDays(deliveryNow.calendarDate, 1);
  }

  function getFirstAvailableDeliveryDate({
    cutoffHour,
    unavailableDates,
    sameDayEligible,
    now = new Date(),
  }) {
    const searchStart = getDeliverySearchStart({ cutoffHour, sameDayEligible, now });
    return getFirstAvailableDateOnOrAfter(searchStart, unavailableDates);
  }

  function getFirstAvailableDateOnOrAfter(startDate, unavailableDates) {
    const blockedDates = new Set(normalizeUnavailableDates(unavailableDates));

    for (let offset = 0; offset < SEARCH_WINDOW_DAYS; offset += 1) {
      const candidate = addCalendarDays(startDate, offset);
      if (getWeekdayIndex(candidate) !== 0 && !blockedDates.has(candidate.key)) return candidate;
    }

    return null;
  }

  function getTranslations() {
  return window.CasaFloralDeliveryConfig?.translations ?? {};
}

function formatAvailabilityLabel(calendarDate, now = new Date()) {
  const deliveryToday = getDeliveryDateTime(now).calendarDate;
  const translations = getTranslations();

  if (calendarDate.key === deliveryToday.key) {
    return translations.availableToday || 'Available Today';
  }

  if (calendarDate.key === addCalendarDays(deliveryToday, 1).key) {
    return translations.availableTomorrow || 'Available Tomorrow';
  }

  const weekday = WEEKDAYS[getWeekdayIndex(calendarDate)];

  return (translations.availableWeekday || 'Available {{ weekday }}')
    .replace('{{ weekday }}', weekday);
}

  function formatDeliveryOptionLabel(calendarDate, now = new Date()) {
    return formatAvailabilityLabel(calendarDate, now).replace(/^Available\s+/, '');
  }

  function formatDeliveryDateShort(calendarDate) {
    return `${calendarDate.day} ${SHORT_MONTHS[calendarDate.month - 1]}`;
  }

  function getConfig() {
    const config = window.CasaFloralDeliveryConfig ?? {};
    return {
      cutoffHour: normalizeCutoffHour(config.cutoffHour),
      unavailableDates: normalizeUnavailableDates(config.unavailableDates),
    };
  }

  function getDeliveryDateOptions({ sameDayEligible, now = new Date() }) {
    const config = getConfig();
    const deliveryToday = getDeliveryDateTime(now).calendarDate;
    const firstAvailableDate = getFirstAvailableDeliveryDate({
      ...config,
      sameDayEligible,
      now,
    });
    const todayAvailable = firstAvailableDate?.key === deliveryToday.key;
    const nextAvailableDate = todayAvailable
      ? getFirstAvailableDateOnOrAfter(addCalendarDays(deliveryToday, 1), config.unavailableDates)
      : firstAvailableDate;

    return {
      today: {
      date: deliveryToday,
      available: todayAvailable,
      label: getTranslations().today || 'Today',
      shortDate: todayAvailable
        ? formatDeliveryDateShort(deliveryToday)
        : (getTranslations().unavailable || 'Unavailable'),
      },
      next: nextAvailableDate
        ? {
            date: nextAvailableDate,
            available: true,
            label: formatDeliveryOptionLabel(nextAvailableDate, now),
            shortDate: formatDeliveryDateShort(nextAvailableDate),
          }
        : {
            date: null,
            available: false,
            label: 'Next date',
            shortDate: 'Unavailable',
          },
    };
  }

  function getAvailabilityElements(root) {
    const elements = [];
    if (root instanceof Element && root.matches('[data-delivery-availability]')) elements.push(root);
    if (root?.querySelectorAll) elements.push(...root.querySelectorAll('[data-delivery-availability]'));
    return elements;
  }

  function decorateDeliveryAvailability(root = document) {
    const elements = getAvailabilityElements(root);
    if (elements.length === 0) return;

    const config = getConfig();
    const now = new Date();
    const results = new Map();

    elements.forEach((element) => {
      const sameDayEligible = element.dataset.sameDay === 'true';
      const resultKey = sameDayEligible ? 'same-day' : 'next-day';

      if (!results.has(resultKey)) {
        results.set(
          resultKey,
          getFirstAvailableDeliveryDate({
            ...config,
            sameDayEligible,
            now,
          }),
        );
      }

      const firstAvailableDate = results.get(resultKey);
      const textElement = element.querySelector('[data-delivery-availability-text]');

      if (!firstAvailableDate || !textElement) {
        element.hidden = true;
        return;
      }

      const availabilityLabel = formatAvailabilityLabel(firstAvailableDate, now);
      const deliveryToday = getDeliveryDateTime(now).calendarDate;
      const isAvailableToday = firstAvailableDate.key === deliveryToday.key;
      textElement.textContent = availabilityLabel;
      element.dataset.deliveryDate = firstAvailableDate.key;
      element.classList.toggle('delivery-availability--today', isAvailableToday);
      element.classList.toggle('delivery-availability--future', !isAvailableToday);
      element.hidden = false;
    });
  }

  function calendarDateFromLocalDate(date) {
    return createCalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }

  function calendarDateToLocalDate(calendarDate) {
    return new Date(calendarDate.year, calendarDate.month - 1, calendarDate.day, 12, 0, 0, 0);
  }

  function formatCalendarDateForInput(calendarDate) {
    return `${String(calendarDate.day).padStart(2, '0')}-${String(calendarDate.month).padStart(2, '0')}-${calendarDate.year}`;
  }

  function dispatchDeliveryDateSelected(picker, dateText, source) {
    picker.dispatchEvent(
      new CustomEvent('casa:delivery-date-selected', {
        bubbles: true,
        detail: { dateText, source },
      }),
    );
  }

  function selectDeliveryDate(calendarDate, picker = document.querySelector('#delivery_date_picker')) {
    if (!calendarDate || !picker || !window.jQuery?.fn?.datepicker) return false;

    const productInfo = picker.closest('product-info');
    const fieldRoot = productInfo ?? document;
    const hiddenInput = fieldRoot.querySelector('#hidden_delivery_date');
    const selectedDateDisplay = fieldRoot.querySelector('#selected_delivery_date');
    const formattedDate = formatCalendarDateForInput(calendarDate);

    picker.dataset.deliveryDateManuallySelected = 'true';
    picker.dataset.deliveryPickerApplyingDefault = 'true';
    try {
      window.jQuery(picker).datepicker('setDate', calendarDateToLocalDate(calendarDate));
    } finally {
      delete picker.dataset.deliveryPickerApplyingDefault;
    }

    if (hiddenInput) hiddenInput.value = formattedDate;
    if (selectedDateDisplay) selectedDateDisplay.textContent = `Selected Delivery Date: ${formattedDate}`;
    dispatchDeliveryDateSelected(picker, formattedDate, 'quick-option');
    return true;
  }

  function applyAutomaticPickerDate(picker, firstAvailableDate, fieldRoot) {
    if (!firstAvailableDate || picker.dataset.deliveryDateManuallySelected === 'true') return;

    const hiddenInput = fieldRoot.querySelector('#hidden_delivery_date');
    const selectedDateDisplay = fieldRoot.querySelector('#selected_delivery_date');
    const firstAvailableInput = fieldRoot.querySelector('#first_available_date');
    const formattedDate = formatCalendarDateForInput(firstAvailableDate);

    picker.dataset.deliveryPickerApplyingDefault = 'true';
    try {
      window.jQuery(picker).datepicker('setDate', calendarDateToLocalDate(firstAvailableDate));
    } finally {
      delete picker.dataset.deliveryPickerApplyingDefault;
    }

    if (hiddenInput) hiddenInput.value = formattedDate;
    if (firstAvailableInput) firstAvailableInput.value = formattedDate;
    if (selectedDateDisplay) selectedDateDisplay.textContent = `Selected Delivery Date: ${formattedDate}`;
  }

  function refreshInitializedDeliveryDatePicker(picker) {
    if (picker.dataset.deliveryDateManuallySelected === 'true') return;

    const productInfo = picker.closest('product-info');
    const fieldRoot = productInfo ?? document;
    const sameDayEligible = productInfo?.dataset.deliverySameDay === 'true';
    const config = getConfig();
    const now = new Date();
    const searchStart = getDeliverySearchStart({ ...config, sameDayEligible, now });
    const firstAvailableDate = getFirstAvailableDeliveryDate({ ...config, sameDayEligible, now });

    window.jQuery(picker).datepicker('option', 'minDate', calendarDateToLocalDate(searchStart));
    applyAutomaticPickerDate(picker, firstAvailableDate, fieldRoot);
  }

  function initializeDeliveryDatePicker(root = document) {
    if (!window.jQuery?.fn?.datepicker) return;

    const picker = root instanceof Element && root.matches('#delivery_date_picker')
      ? root
      : root.querySelector?.('#delivery_date_picker');

    if (!picker) return;
    if (picker.dataset.deliveryPickerInitialized === 'true') {
      refreshInitializedDeliveryDatePicker(picker);
      return;
    }

    const productInfo = picker.closest('product-info');
    const fieldRoot = productInfo ?? document;
    const hiddenInput = fieldRoot.querySelector('#hidden_delivery_date');
    const selectedDateDisplay = fieldRoot.querySelector('#selected_delivery_date');
    const sameDayEligible = productInfo?.dataset.deliverySameDay === 'true';
    const config = getConfig();
    const now = new Date();
    const searchStart = getDeliverySearchStart({ ...config, sameDayEligible, now });
    const firstAvailableDate = getFirstAvailableDeliveryDate({ ...config, sameDayEligible, now });
    const blockedDates = new Set(config.unavailableDates);

    window.jQuery(picker).datepicker({
      dateFormat: 'dd-mm-yy',
      minDate: calendarDateToLocalDate(searchStart),
      beforeShowDay(date) {
        const candidate = calendarDateFromLocalDate(date);
        return [getWeekdayIndex(candidate) !== 0 && !blockedDates.has(candidate.key)];
      },
      onSelect(dateText) {
        if (picker.dataset.deliveryPickerApplyingDefault !== 'true') {
          picker.dataset.deliveryDateManuallySelected = 'true';
        }
        if (hiddenInput) hiddenInput.value = dateText;
        if (selectedDateDisplay) selectedDateDisplay.textContent = `Selected Delivery Date: ${dateText}`;
        dispatchDeliveryDateSelected(picker, dateText, 'calendar');
      },
    });

    picker.dataset.deliveryPickerInitialized = 'true';
    applyAutomaticPickerDate(picker, firstAvailableDate, fieldRoot);
  }

  function initializeDeliveryAvailability(root = document) {
    decorateDeliveryAvailability(root);
    initializeDeliveryDatePicker(root);
    document.dispatchEvent(new CustomEvent('casa:delivery-availability-refreshed'));
  }

  function getDeliveryRefreshState(now, cutoffHour) {
    const deliveryNow = getDeliveryDateTime(now);
    const cutoffState = deliveryNow.hour < normalizeCutoffHour(cutoffHour) ? 'before-cutoff' : 'cutoff-reached';
    return `${deliveryNow.calendarDate.key}:${cutoffState}`;
  }

  function findNextDeliveryBoundary(now, cutoffHour) {
    const currentState = getDeliveryRefreshState(now, cutoffHour);
    let currentTimestamp = now.getTime();
    let boundaryTimestamp = currentTimestamp + 26 * 60 * 60 * 1000;

    if (getDeliveryRefreshState(new Date(boundaryTimestamp), cutoffHour) === currentState) return null;

    while (boundaryTimestamp - currentTimestamp > 1000) {
      const candidateTimestamp = Math.floor((currentTimestamp + boundaryTimestamp) / 2);
      const candidateState = getDeliveryRefreshState(new Date(candidateTimestamp), cutoffHour);

      if (candidateState === currentState) {
        currentTimestamp = candidateTimestamp;
      } else {
        boundaryTimestamp = candidateTimestamp;
      }
    }

    return new Date(boundaryTimestamp);
  }

  function scheduleDeliveryRefresh() {
    window.clearTimeout(deliveryRefreshTimer);

    const now = new Date();
    const nextBoundary = findNextDeliveryBoundary(now, getConfig().cutoffHour);
    if (!nextBoundary) return;

    const delay = Math.max(0, nextBoundary.getTime() - now.getTime() + 50);
    deliveryRefreshTimer = window.setTimeout(() => {
      initializeDeliveryAvailability(document);
      scheduleDeliveryRefresh();
    }, delay);
  }

  function refreshAfterTabActivation() {
    initializeDeliveryAvailability(document);
    scheduleDeliveryRefresh();
  }

  function initializeDeliverySystem() {
    initializeDeliveryAvailability(document);
    scheduleDeliveryRefresh();
  }

  window.CasaFloralDelivery = {
    getFirstAvailableDeliveryDate,
    getDeliveryDateOptions,
    formatAvailabilityLabel,
    formatCalendarDateForInput,
    decorateDeliveryAvailability,
    initializeDeliveryDatePicker,
    selectDeliveryDate,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDeliverySystem, { once: true });
  } else {
    initializeDeliverySystem();
  }

  document.addEventListener('shopify:section:load', (event) => initializeDeliveryAvailability(event.target));
  document.addEventListener('product-info:loaded', (event) => initializeDeliveryAvailability(event.target));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshAfterTabActivation();
  });
  window.addEventListener('focus', refreshAfterTabActivation);
})();
